define((require) => {
    "use strict";

    var global      = require("channels/Global"),
        util        = require("channels/Util"),
        format      = require("channels/Format"),
        state       = require("channels/State"),
        MathUtil    = require("shared/util/MathUtil").default,
        Log         = require("channels/Log");

    var SEQUENCE = 1; // to keep track of number of docs for easy debugging.

    /**
     * @class {Dom}
     *
     * Defines a "mini dom" used to manipulate the dom, only exposing the minimum set of channel
     * required dom methods. <p>
     *
     * Multiple doms can exist, one per channel, to support channels embedding other channels on
     * the fly (i.e. store the dom for the iframe in question). <p>
     *
     * Several dom methods are static, i.e. all those not requiring direct access to the current
     * document/window, like <tt>Dom.data</tt>.
     *
     * @param {Window|HTMLIframeElement} win The window owning the document in question; cannot be falsey.
     *
     * @author Gunni Rode  / <a href="http://www.zmags.com">Zmags</a>
     * @author Kim Knudsen / <a href="http://www.zmags.com">Zmags</a>
     */
    function Dom(win) {
        // Handle iframe...
        if (win.contentWindow) {
            win = win.contentWindow;
        }

        var dom = this;

        /**
         * @private @type {Number}
         * Dom sequence number, used only for easy debugging.
         */
        this._id = SEQUENCE++;

        /** @type {Window} The window containing this dom; never null. */
        this.win = win;

        /** @type {Document} The document/dom, where the body may/will change. */
        this.doc = win.document; // ok now, since "win" is a window type!

        /**
         * Returns true if this dom represents a document in a non-top level window, false otherwise. <p>
         *
         * Note that an embedded channel can still use the top level dom, if for example opened in a lightbox
         * attached to the top level document.
         *
         * @return {Boolean} True if embedded, false otherwise.
         *
         * @see    Channel#embedded
         */
        this.embedded = function() {
            return win !== global.win;
        };

        /**
         * Returns the 'head' element for the specified document, defaulting to the
         * global document.
         *
         * @return {HTMLElement} The 'head' element; never falsey.
         */
        this.head = function() {
            // The head will never change, even for single page apps, but legacy browsers have no "head" property...
            return dom.doc.head || dom.doc.getElementsByTagName("head")[0];
        };

        /**
         * Returns a reference to the body element, or falsey if the body has not been initialized.
         *
         * @return {HTMLElement} A reference to the body element, or falsey if the body
         *                       has not been initialized.
         */
        this.body = function() {
            return dom.doc && (dom.doc.body || dom.doc.getElementsByTagName("body")[0]);
        };

        /**
         * Returns true if the document for this dom contains the specified element, false otherwise.
         *
         * @param {HTMLElement} element The element to test; can be falsey, in which case this
         *                              function returns false.
         *
         * @return {Boolean} True if <tt>element</tt> is contained, false otherwise.
         */
        this.contains = function(element) { // IE trouble for iframe documents
            var body = dom.body(),
                i;

            if (body && element) {
                if (body.contains) {
                    return body.contains(element);
                } else if (body.all) { // IE iframe docs...
                    i = body.all.length;
                    while (i--) {
                        if (body.all[i] === element) {
                            return true;
                        }
                    }
                    // Fall-through
                }
            }
            return false;
        };

        /**
         * Returns the current executing script at invocation time. <p>
         *
         * Caveat: this <b>only</b> works when the document is loading and called in inline script tags, but
         * <b>not</b> if called after the document has finished loading; in the latter case, the returned script
         * will always be the last script in the dom.
         *
         * @return {HTMLElement} The current executing script at invocation time while the document is loading,
         *                       otherwise falsey.
         *
         * @see #currentElement
         * @see Channel#inPlace
         */
        this.currentScript = function() {
            // If "currentScript" not supported and document has already loaded, we return falsey instead of some
            // random script...
            return dom.doc.currentScript || (!dom.docLoaded && ((function() {
                var scripts = dom.doc.getElementsByTagName("script");
                return scripts[scripts.length - 1];
            })()));
        };

        /**
         * Returns the current element deduced from the script calling this method while the document is loading.
         * More precisely, it is the parent element of the current script executing. <p>
         *
         * Caveat: this <b>only</b> works when the document is loading and called in inline script tags, but
         * <b>not</b> if called after the document has finished loading; in the latter case, the returned element
         * will always be the parent element of the last script in the dom.
         *
         * @return {HTMLElement} The current element as described above, if any; can be null.
         *
         * @see #currentScript
         * @see Channel#inPlace
         */
        this.currentElement = function() {
            var script = dom.currentScript();
            return script ? script.parentElement : null;
        };

        /**
         * Adds the specified callback to be invoked once the dom is ready; do NOT use this for non-document elements.
         * The callback will only be invoked one time. <p>
         *
         * Options format:
         * <pre>
         * {
         *     name:     String,    // the function called.
         *     scope:    Log        // calling scope, also potentially doubling as a logger (or defaulting to global log).
         *     callback: Function,  // the no-arg callback to invoked on dom ready
         *     args:     *|[]       // single argument for "callback", if any (can be an array).
         * }
         * </pre>
         *
         * If the dom is ready at invocation time, <tt>options.callback</tt> is invoked immediately.
         *
         * @param {*} options The on-ready options; cannot be null.
         *
         * @see   #ready
         */
        this.docReady = function(options) {
            var scope = options.scope,
                log = scope && scope.verbose ? scope : Log.global(),
                callback = function() {
                    log.verbose("Dom ready callback: ", format.val(options.name), dom);
                    options.callback(options.args); // already scoped
                    // Since we know the dom is ready, we can call the callback directly henceforth if such
                    // a callback is defined for the scope, e.g. "populateChannels"...
                    if (scope && scope[options.name]) {
                        scope[options.name] = options.callback;
                    }
                };

            // If we know for sure the document is ready, we do not bother with event handlers...
            if (dom.docLoaded) {
                callback();
                return;
            }

            // Make sure the callback is only invoked once, we assign it to multiple events...
            var invoke = util.one(log, options.name, callback);

            // Use the handy event callback, or fallback to window that will always work (slower). We will assign both
            // unless already ready...
            if (Dom.on(log, dom.doc, dom.doc.addEventListener ? "DOMContentLoaded" : "readystatechange", invoke)) {
                Dom.on(log, win, "load", invoke);
            }
        };

        /**
         * Returns the elements matched by the passed CSS selector from the document associated with this dom. <p>
         *
         * <tt>selector</tt> is <b>not</b> a jQuery selector, but a native one, so we use <tt>querySelectorAll</tt>
         * (for non-legacy browsers). Otherwise we default to our custom Sizzle, expected to be ready in the sandbox
         * at the time of invocation.
         *
         * @param   {String} selector The native CSS selector used; never falsey.
         * @returns {NodeList} All matched elements; can be empty.
         * @throws  {Error} If <tt>selector</tt> is invalid, or if legacy browser and Sizzle is not loaded yet.
         */
        this.selectElements = function(selector) {
            selector = Dom.escapeSelector(selector);

            // Custom Sizzle is (already) loaded when no native CSS selector support...
            return global.capabilities.isQuerySelectorSupported ?
                dom.doc.querySelectorAll(selector) :
                global.sandbox().sizzle(selector, dom.doc);
        };

        /**
         * Returns a new iframe with src "about:blank" with a display style of "none", attached to the current
         * body if already loaded, otherwise unattached.
         *
         * @return {HTMLIFrameElement} The created iframe, possibly attached; never null. To test if the iframe is
         *                             attached, query for its "contentDocument" property (will be available if
         *                             attached).
         */
        this.iframe = function() {
            var iframe = dom.createElement("iframe", {src: "about:blank"});
            iframe.style.display = "none";

            var body = dom.body();
            if (body) {
                body.appendChild(iframe);
            }

            return iframe;
        };

        /**
         * Constructs an <tt>HTMLStyleElement</tt> containing the specified CSS string.
         * The element is appended to the head of the current context.
         *
         * @param  {String} css A valid CSS string. E.g. <tt>.zmags-node {opacity: 0;}</tt>.
         */
        this.css = function(css) {
            var style = dom.doc.createElement("style");
            style.type = "text/css";

            if (style.styleSheet) {
                style.styleSheet.cssText = css;
            } else {
                style.appendChild(dom.doc.createTextNode(css));
            }

            dom.head().appendChild(style);
        };

        /**
         * Creates a new detached dom element with the specified tag, using the document associated with this dom.
         *
         * @param  {String}      tag          The tag name of the element to create, e.g. "div", "iframe", etc.
         * @param  {*}           [attributes] Optional attributes to set on the created element, where those with a
         *                                    falsey value will be ignored.
         * @param  {String}      [className]  Optional class name to append to the element's class list; can be falsey.
         * @return {HTMLElement} The created element; never falsey.
         */
        this.createElement = function(tag, attributes, className) {
            var element = dom.doc.createElement(tag),
                name,
                value;

            if (attributes) {
                for (name in attributes) {
                    value = attributes[name];
                    if (value) {
                        element.setAttribute(name, value);
                    }
                }
            }

            if (className) {
                Dom.addClass(element, className);
            }

            return element;
        };

        /**
         * Returns the bounds for the specified element, if supplied, or the bounds for the browsers viewport. <p>
         *
         * Different browsers may give different results! Also note that properties such as "left" can be negative,
         * e.g. if currently positioned outside the actual browser viewport and thus not visible to the end user.
         *
         * @param  {HTMLElement} [element] The element to get the bounds for; can be falsey, in which case the bounds
         *                                 for the browsers viewport is returned.
         * @return {{width:Number, height:Number, left:Number, top:Number, right:Number, bottom:Number}} The bounds;
         *         never null, but individual properties may be undefined, i.e. not available.
         */
        this.bounds = function(element) {
            var bounds = {};
            // Element...
            if (element) {
                if (element.getBoundingClientRect) {
                    bounds = element.getBoundingClientRect() || {};
                }
                // Legacy IE returns a client rect, but without width and height, and it cannot be updated
                // so we have to make a copy with width and height... :/
                if (typeof bounds.width === "undefined" || typeof bounds.height === "undefined") {
                    bounds = {
                        width:  element.offsetWidth  || (bounds.right - bounds.left), // also works for negative values
                        height: element.offsetHeight || (bounds.bottom - bounds.top),
                        top:    bounds.top,
                        left:   bounds.left,
                        bottom: bounds.bottom,
                        right:  bounds.right
                    };
                }

            // Browser (window) viewport...
            } else {
                bounds = {top: 0, left: 0};
                // Prefer jQuery calculation if available since it should take everything into account...
                var $ = global.$(),
                    win,
                    width,
                    height;

                if ($) {
                    win    = $(global.win);
                    width  = win.width(); // jQuery defines these!
                    height = win.height();
                }
                bounds.width  = bounds.right  = width  || global.win.innerWidth;
                bounds.height = bounds.bottom = height || global.win.innerHeight;
            }
            return bounds;
        };

        /**
         * Computes the visible (vertical) viewport for the specified target, relative to *this* DOM.
         *
         * @param  {HTMLElement} target The target element.
         * @return {VerticalBounds}     The <tt>VerticalBounds</tt>.
         *
         * @see  MathUtil#computeVerticalBounds
         */
        this.calculateVerticalBounds = function(target) {
            var viewportBounds = this.bounds(),
                targetBounds = this.bounds(target);

            viewportBounds.top = this.body().scrollTop;

            return MathUtil.computeVerticalBounds(targetBounds, viewportBounds);
        };

        this.toString = function() {
            return "Dom[#" + dom._id + "; ready: " + dom.docLoaded + "]";
        };

        /**
         * @type {Boolean}
         * Constant set to true when the document has loaded, false until then.
         */
        this.docLoaded = false;

        // Update our handy constant when document is ready...
        dom.docReady({name: "docLoaded", callback: function() { dom.docLoaded = true; }});
    }

    // STATIC

    /**
     * Factory method that returns a dom backed by the supplied window, defaulting to the current window.
     *
     * @param {Window|HTMLIframeElement} [win] The window owning the dom (document) in question; will default to
     *                                         the current window.
     * @return {Dom} The corresponding dom instance; never falsey.
     */
    Dom.of = function(win) {
        win = win || window;
        return win === (state.dom && state.dom.win) ? state.dom : new Dom(win);
    };

    /**
     * @static
     * Gets, sets, or clears the named "data" attribute from the specified dom element.
     *
     * @param {HTMLElement} element  The dom element to get/set/clear the "data" attribute for; cannot be null.
     * @param {String}      name     The attribute name, <b>excluding</b> prefixed "data-"; cannot be null.
     * @param {*}           [value]  The value to set the string value of, if any; null means clear, any other value
     *                               will be set. If no supplied at all, the data will be returned.
     * @return {String} The "data" attribute for the specified dom element (will be <tt>value</tt> if supplied);
     *                  never null.
     */
    Dom.data = function(element, name, value) {
        name = "data-" + name;
        if (arguments.length === 2) {
            value = element.getAttribute(name) || "";
        } else if (value === null) {
            element.removeAttribute(name);
        } else {
            element.setAttribute(name, "" + value);
        }
        return value;
    };

    /**
     * Returns the computed style property for the specified dom element.
     *
     * @param  {HTMLElement} element  The dom element to find the property for; cannot be null.
     * @param  {String}      property The property name; cannot be null.
     * @return {String} The specified property value as a string, or falsey if no such property.
     */
    Dom.style = function(element, property) {
        var value = null;
        // IE will throw an error if the passed element can't have styles (such as a text node)
        try {
            value = global.win.getComputedStyle(element).getPropertyValue(property);
        } catch (e) {
            // Fall-through - value will be null...
        }
        return value;
    };

    /**
     * @static
     * Sets the visibility for the supplied dom element.
     *
     * @param {HTMLElement} [element]    The element to set the visibility for; this function does nothing if falsey.
     * @param {String}      [visibility] The visibility to set for "element" as "visible", "hidden", "inherit",
     *                                   "none" or "block". The latter two will set the "display" property, the
     *                                   former the "visibility" property. This function does nothing if falsey.
     */
    Dom.visibility = function(element, visibility) {
        if (element && visibility) {
            // This also applies to legacy browsers...
            var prop = visibility === "none" || visibility === "block" ? "display" : "visibility";
            element.style.cssText += "; " + prop + ": " + visibility + " !important";
        }
    };

    /**
     * Returns the elements matched by the passed CSS selector from the dom associated with the specified
     * window, defaulting to the current window. <p>
     *
     * <tt>selector</tt> is <b>not</b> a jQuery selector, but a native one, so we use <tt>querySelectorAll</tt>
     * (for non-legacy browsers). Otherwise we default to our custom Sizzle, expected to be ready in the sandbox
     * at the time of invocation.
     *
     * @param   {String}                   selector The native CSS selector used; cannot be falsey.
     * @param   {Window|HTMLIframeElement} [win]    The window owning the dom (document) in question; will default to
     *                                              the current window.
     * @returns {NodeList} All matched elements from the dom associated with the (defaulted) window; can be empty.
     * @throws  {Error} If <tt>selector</tt> is invalid, or if legacy browser and Sizzle is not loaded yet.
     */
    Dom.selectElements = function(selector, win) {
        return Dom.of(win).selectElements(selector);
    };

    /**
     * @static
     * Removes the specified element from it's parent, if any.
     *
     * @param  {HTMLElement} element The element to remove; if falsey, this function does nothing.
     */
    Dom.removeElement = function(element) {
        var parent = element && element.parentNode;
        if (parent) {
            // This can cause trouble if we are removing the Viewer container while it is still loading in admin
            // mode, since the script(s) will fail hard when their context (iframe) window is destroyed. Because
            // of this this, the admin code is cautious when it will populate channels to avoid such scenarios...
            parent.removeChild(element);
        }
    };

    /**
     * @static
     * Adds the specified class name to the given element.
     *
     * @param {HTMLElement} element   The target element.
     * @param {String}      className The class name to append to the element's class list.
     */
    Dom.addClass = function(element, className) {
        if (element.classList) {
            element.classList.add(className);
        } else {
            element.className += " " + className;
        }
    };

    /**
     * @static
     * Takes any native selector and escapes it properly in case it contains numbers in reserved positions,
     * e.g. at the start of any individual selector. <p>
     *
     * Handles:
     * <ul>
     * <li> Single id or class (#foo)
     * <li> Combined (#foo.bar)
     * <li> Descendant (#foo .bar)
     * <li> Immediate child (#foo>.bar)
     * </ul><p>
     *
     * This function is really dom agnostic ("static"); <tt>element</tt> does not have to belong to this dom.
     *
     * @param {String} selector The selector to be escaped; cannot be null/undefined.
     * @return {String} The escaped selector; never null/undefined.
     * @throws {Error} If <tt>selector</tt> is null/undefined.
     */
    Dom.escapeSelector = function(selector) {
        // The function supplied for each match takes 3 arguments: "all" = entire match, e.g. ".12foo" (not used),
        // "escaped" = the selector id or class prefix, e.g. ".", and "digits" = the digits (string!) immediately
        // following the class or id selector, e.g. "12"...
        return selector.replace(/([#.])(\d+)/g, (all, escaped, digits) => {
            for (var i = 0; i < digits.length; i++) {
                escaped += "\\3" + digits[i];
            }
            return escaped + " "; // escaping ends with a space!
        });
    };

    /**
     * @static
     * Returns true if <tt>descendant</tt> is actually a descendant of <tt>element</tt>, including if
     * <tt>element</tt> itself, otherwise false.
     *
     * @param {HTMLElement} element    The root element; can be falsey, in which case false is returned.
     * @param {HTMLElement} descendant The descendant to check; can be falsey, in which case false is returned.
     * @return {Boolean} True if <tt>descendant</tt> is a descendant of or <tt>element</tt> itself, false otherwise.
     */
    Dom.descendant = function(element, descendant) {
        var node = descendant;
        while (node) {
            if (element === node) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    };

    /**
     * @static
     * Adds <tt>fn</tt> as a <b>one-time</b> event handler on <tt>element</tt> for the <tt>name</tt>d event.
     *
     * @param  {Log}                  log     The log to use; cannot be falsey.
     * @param  {HTMLElement|Window|*} element The html element, document, or window to add the handler to; cannot be null.
     * @param  {String}               name    The event name, minus "on" prefix; cannot be null.
     * @param  {Function}             fn      The function to call at most one time when <tt>element</tt> is ready;
     *                                        cannot be null. The function is assumed to be scoped and will be called
     *                                        without the <tt>Event</tt> argument only, or no args if already ready.
     *
     * @return {Boolean} False if <tt>element</tt> is already ready and <tt>fn</tt> has been called once this
     *                   function returns, true otherwise.
     */
    Dom.on = function(log, element, name, fn) {
        // Already ready? Works for "document" in all major browsers, also for elements in IE...
        var state = element.readyState; // ignored for "window" object!
        if (state === "complete") {
            fn();
            return false;
        }

        var callback;
        // Modern browsers, e.g. Chrome...
        if (element.addEventListener) {
            callback = function(event) {
                element.removeEventListener(name, callback, false);
                fn(event);
            };
            element.addEventListener(name, callback, false);

        // Legacy browsers, i.e. old Explorer. We add more logging here, since old legacy browsers are
        // way less stable...
        } else if (element.attachEvent) {
            // Windows does not support readyState, so we have to determine if a window! And of course iframe windows
            // in IE are not a proper Window type, so we have to do it like this... :/
            var win = element.toString() === "[object Window]";
            name = "on" + name;
            callback = function(event) {
                state = element.readyState; // undefined for windows
                if (state === "loaded" || state === "complete" || win) { // "complete" when cached content!
                    element.detachEvent(name, callback);
                    if (win) {
                        log.verbose("Window ready");
                    } else {
                        log.verbose("<" + element.tagName || element.nodeName + "> ready: ", format.val(state), element);
                    }
                    fn(event || element.event); // element.event = window.event
                }
            };
            element.attachEvent(name, callback);
        } else {
            log.error("Cannot add callback: ", format.val(element.tagName + "." + name), element);
        }
        return true;
    };

    /**
     * @static
     * Adds the specified callback to be invoked once the specified "loadable" element is ready; do NOT use this
     * for the global dom, only for specific dom elements that can load content like <script> and <image> (after
     * the dom is known to be ready). The callback will only be invoked one time. <p>
     *
     * Options format:
     * <pre>
     * {
     *     element:  HTMLElement, // the dom element; never document, but can be script or image.
     *     name:     String,      // the function called.
     *     scope:    Log          // calling scope, also potentially doubling as a logger (or defaulting to global log).
     *     callback: Function,    // the callback to invoked on ready (see "args" below).
     *     args:     *|[],        // single argument for "callback", if any (can be an array); will default to
     *                            // the <tt>Event</tt> in question.
     *     errback:  Function     // optional handler to be called on error with the <tt>Event</tt> as the single
     *                            // argument, not "args"!
     * }
     * </pre>
     *
     * If the element is ready at invocation time, <tt>options.callback</tt> is invoked immediately. <p>
     *
     * This function is really dom agnostic ("static"); <tt>element</tt> does not have to belong to this dom.
     *
     * @param {*} options The on-ready options; cannot be null.
     *
     * @see   #docReady
     */
    Dom.ready = function(options) {
        // Make sure the callback is only invoked once, we assign it to multiple events...
        var log = options.scope.verbose ? options.scope : Log.global(),
            invoke = util.one(log, options.name, (event) => {
                log.verbose("<" + options.element.tagName + "> ready callback: ", format.val(options.name));
                if (options.callback) {
                    options.callback(options.args || event); // already scoped
                }
            });

        // Add ready handler, and optional errback, but only if the content is not already ready...
        if (Dom.on(log, options.element, options.element.addEventListener ? "load" : "readystatechange", invoke) && options.errback) { // IE11 and forward now uses "onload" as well!
            Dom.on(log, options.element, "error", (event) => {
                invoke.clear(); // clear timer, we have an errback to handle this, i.e. we know it is erroneous...
                options.errback(event);
            });
        }
    };

    return Dom;
});



// WEBPACK FOOTER //
// ./src/channels/channels/Dom.js